import { PublicKey, Transaction } from '@solana/web3.js';

type SignCanonicalTransactionArgs = {
  transaction: Transaction;
  publicKey: PublicKey;
  signTransaction?: (transaction: Transaction) => Promise<Transaction>;
  signMessage?: (message: Uint8Array) => Promise<Uint8Array>;
  expectedMessageB64?: string;
};

const getMessageB64 = (transaction: Transaction) =>
  Buffer.from(transaction.serializeMessage()).toString('base64');

const COMPUTE_BUDGET_PROGRAM_ID = 'ComputeBudget111111111111111111111111111111';
const WALLET_PROTECTION_PROGRAM_ID =
  'L2TExMFKdjpN9kozasaurPirfHy9P8sbXoAN1qA3S95';
const ALLOWED_SUPPLEMENTAL_PROGRAM_IDS = new Set([
  COMPUTE_BUDGET_PROGRAM_ID,
  WALLET_PROTECTION_PROGRAM_ID
]);

const getCanonicalInstructions = (transaction: Transaction) =>
  transaction.instructions
    .filter(
      (instruction) =>
        !ALLOWED_SUPPLEMENTAL_PROGRAM_IDS.has(instruction.programId.toBase58())
    )
    .map((instruction) => ({
      programId: instruction.programId.toBase58(),
      keys: instruction.keys.map((key) => ({
        pubkey: key.pubkey.toBase58(),
        isSigner: key.isSigner,
        isWritable: key.isWritable
      })),
      data: Buffer.from(instruction.data).toString('base64')
    }));

const getTransactionIntentFingerprint = (transaction: Transaction) =>
  JSON.stringify({
    feePayer: transaction.feePayer?.toBase58() ?? null,
    instructions: getCanonicalInstructions(transaction)
  });

export const signCanonicalTransaction = async ({
  transaction,
  publicKey,
  signTransaction,
  signMessage,
  expectedMessageB64
}: SignCanonicalTransactionArgs) => {
  const messageBytes = transaction.serializeMessage();
  const messageB64 = Buffer.from(messageBytes).toString('base64');

  if (expectedMessageB64 && messageB64 !== expectedMessageB64) {
    throw new Error(
      'Transaction changed before signing. Please recreate the transaction.'
    );
  }

  const expectedIntent = getTransactionIntentFingerprint(transaction);
  let signedTransaction: Transaction;

  if (signTransaction) {
    signedTransaction = await signTransaction(transaction);
  } else if (signMessage) {
    const signature = await signMessage(messageBytes);
    transaction.addSignature(publicKey, Buffer.from(signature));
    signedTransaction = transaction;
  } else {
    throw new Error('Your wallet does not support transaction signing.');
  }

  if (
    expectedMessageB64 &&
    getMessageB64(signedTransaction) !== expectedMessageB64
  ) {
    const signedIntent = getTransactionIntentFingerprint(signedTransaction);

    if (expectedIntent === signedIntent) {
      return signedTransaction;
    }

    throw new Error(
      'Your wallet changed the transaction instructions while signing. Please disable wallet transaction modification features or try another wallet.'
    );
  }

  return signedTransaction;
};
